/**
 * RollupSetupStep (Story 40-6 — Tasks 4 / AC1, AC2, AC3).
 *
 * The new FIRST step of the report-creation wizard: pick a report KIND
 * (Client vs Roll-up), and for a roll-up its SCOPE (Brand vs Portfolio) plus
 * the member set. The single-client path is unchanged — choosing "Client report"
 * just advances the existing wizard. This component owns ONLY the roll-up
 * sub-flow (the client selector lives in the existing template step).
 *
 *  - Brand: lists brands (name + memberCount + owner badge when ownerContactEmail
 *    is set). Selecting a brand resolves its members DYNAMICALLY via
 *    `useBrand(id).clients` (never a frozen list). A 0-member brand is blocked
 *    inline.
 *  - Portfolio: a searchable multi-select client picker (clones the
 *    ManageBrandClientsDialog checkbox pattern off the shared ["clients"] query).
 *    ≥1 member required; >ROLLUP_MEMBER_CAP blocked. Demo clients are selectable.
 *
 * The parent persists the selection in the route search params and gates the
 * "Continue" affordance; this component surfaces the current selection + the
 * inline guard messages and calls back on every change.
 */

import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, MagnifyingGlass, Buildings, UsersThree, FileText } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { apiGet } from "../../lib/apiClient";
import { useBrands, useBrand } from "../../hooks/useBrands";
import type { Client } from "../clients/types";
import { ROLLUP_MEMBER_CAP } from "./rollupPaletteGate";

export type ReportKind = "client" | "rollup";
export type RollupScope = "brand" | "portfolio";

interface RollupSetupStepProps {
  /** "client" | "rollup" | null — null = no kind chosen yet. */
  kind: ReportKind | null;
  rollupScope: RollupScope | null;
  /** Selected brand id (brand scope). */
  brandId: string | null;
  /** Selected portfolio member client ids (portfolio scope). */
  memberClientIds: string[];
  onSelectKind: (kind: ReportKind) => void;
  onSelectScope: (scope: RollupScope) => void;
  onSelectBrand: (brandId: string) => void;
  onPortfolioChange: (memberClientIds: string[]) => void;
}

export function RollupSetupStep({
  kind,
  rollupScope,
  brandId,
  memberClientIds,
  onSelectKind,
  onSelectScope,
  onSelectBrand,
  onPortfolioChange,
}: RollupSetupStepProps) {
  return (
    <div className="max-w-3xl mx-auto" data-testid="rollup-setup-step">
      <div className="text-center mb-8">
        <h2 className="text-[21px] font-semibold tracking-tight">What kind of report?</h2>
        <p className="text-[15px] text-foreground/70 mt-1">
          Build a single-client report, or roll one metric up across many clients.
        </p>
      </div>

      {/* Kind picker — Client vs Roll-up */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <KindCard
          testId="report-kind-client"
          active={kind === "client"}
          icon={<FileText size={22} weight="duotone" />}
          title="Client report"
          description="A report for a single client — the standard flow."
          onClick={() => onSelectKind("client")}
        />
        <KindCard
          testId="report-kind-rollup"
          active={kind === "rollup"}
          icon={<Buildings size={22} weight="duotone" />}
          title="Roll-up report"
          description="One consolidated report across a brand's clients or an ad-hoc set."
          onClick={() => onSelectKind("rollup")}
        />
      </div>

      {/* Roll-up scope + member picker */}
      {kind === "rollup" && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
          className="mt-8"
        >
          <h3 className="text-[15px] font-semibold mb-3 text-center">Choose what to roll up</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <KindCard
              testId="rollup-scope-brand"
              active={rollupScope === "brand"}
              icon={<Buildings size={20} weight="duotone" />}
              title="Brand"
              description="Every client under a brand — tracks the brand's current members."
              onClick={() => onSelectScope("brand")}
            />
            <KindCard
              testId="rollup-scope-portfolio"
              active={rollupScope === "portfolio"}
              icon={<UsersThree size={20} weight="duotone" />}
              title="Portfolio"
              description="An ad-hoc set of clients you pick by hand."
              onClick={() => onSelectScope("portfolio")}
            />
          </div>

          {rollupScope === "brand" && (
            <BrandPicker selectedBrandId={brandId} onSelectBrand={onSelectBrand} />
          )}
          {rollupScope === "portfolio" && (
            <PortfolioPicker selected={memberClientIds} onChange={onPortfolioChange} />
          )}
        </motion.div>
      )}
    </div>
  );
}

// ── Kind / scope card ────────────────────────────────────────────────

function KindCard({
  active,
  icon,
  title,
  description,
  onClick,
  testId,
}: {
  active: boolean;
  icon: React.ReactNode;
  title: string;
  description: string;
  onClick: () => void;
  testId: string;
}) {
  return (
    <button
      type="button"
      data-testid={testId}
      aria-pressed={active}
      onClick={onClick}
      className={`flex flex-col items-start gap-2 rounded-2xl p-5 text-left transition-all cursor-pointer bg-card shadow-xs hover:-translate-y-0.5 hover:shadow-sm ${
        active ? "ring-2 ring-primary" : ""
      }`}
      style={{ border: "0.5px solid var(--hairline)" }}
    >
      <span className="text-primary">{icon}</span>
      <span className="text-[15px] font-semibold">{title}</span>
      <span className="text-[13px] text-foreground/70">{description}</span>
    </button>
  );
}

// ── Brand picker (AC2) ───────────────────────────────────────────────

function BrandPicker({
  selectedBrandId,
  onSelectBrand,
}: {
  selectedBrandId: string | null;
  onSelectBrand: (brandId: string) => void;
}) {
  const { data: brands = [], isLoading, isError } = useBrands();
  // Resolve the SELECTED brand's CURRENT members dynamically (never frozen).
  const { data: selectedBrand } = useBrand(selectedBrandId ?? undefined);

  return (
    <div className="mt-6" data-testid="rollup-brand-picker">
      <h4 className="text-[13px] font-semibold mb-2">Select a brand</h4>
      {isLoading ? (
        <p className="text-sm text-muted-foreground py-4 text-center">Loading brands…</p>
      ) : isError ? (
        <p className="text-sm text-destructive py-4 text-center">Failed to load brands.</p>
      ) : brands.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4 text-center" data-testid="rollup-brands-empty">
          No brands yet. Create a brand and assign clients to it first.
        </p>
      ) : (
        <div className="space-y-1.5">
          {brands.map((brand) => {
            const isSelected = brand.id === selectedBrandId;
            return (
              <button
                key={brand.id}
                type="button"
                data-testid={`rollup-brand-option-${brand.id}`}
                aria-pressed={isSelected}
                onClick={() => onSelectBrand(brand.id)}
                className={`w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors cursor-pointer bg-background shadow-xs ${
                  isSelected ? "ring-2 ring-primary" : "hover:bg-accent/30"
                }`}
                style={{ border: "0.5px solid var(--hairline)" }}
              >
                <Buildings size={18} className="shrink-0 text-muted-foreground" weight="duotone" />
                <span className="flex-1 truncate text-[13.5px] font-medium">{brand.name}</span>
                {brand.ownerContactEmail && (
                  <span
                    className="shrink-0 text-[10.5px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary"
                    data-testid={`rollup-brand-owner-badge-${brand.id}`}
                  >
                    Shareable
                  </span>
                )}
                <span className="shrink-0 text-[11px] text-muted-foreground">
                  {brand.memberCount} {brand.memberCount === 1 ? "client" : "clients"}
                </span>
                {isSelected && (
                  <span className="shrink-0 w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                    <Check size={12} weight="bold" className="text-primary-foreground" />
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* 0-member brand guard (AC2): blocks proceeding. */}
      {selectedBrandId && selectedBrand && selectedBrand.clients.length === 0 && (
        <p
          className="mt-3 text-[13px] text-amber-700 bg-amber-50 border border-amber-300 rounded-lg px-3 py-2"
          data-testid="rollup-brand-empty-warning"
        >
          This brand has no clients yet — assign clients to it first.
        </p>
      )}
      {selectedBrandId && selectedBrand && selectedBrand.clients.length > 0 && (
        <p className="mt-3 text-[12px] text-muted-foreground" data-testid="rollup-brand-member-summary">
          Rolls up {selectedBrand.clients.length}{" "}
          {selectedBrand.clients.length === 1 ? "client" : "clients"}:{" "}
          {selectedBrand.clients.map((c) => c.name).join(", ")}
        </p>
      )}
    </div>
  );
}

// ── Portfolio picker (AC3) — clones the ManageBrandClientsDialog checkbox list ──

function PortfolioPicker({
  selected,
  onChange,
}: {
  selected: string[];
  onChange: (memberClientIds: string[]) => void;
}) {
  const [search, setSearch] = useState("");
  const { data: clients = [], isLoading } = useQuery<Client[]>({
    queryKey: ["clients"],
    queryFn: async () => {
      const res = await apiGet("/api/clients");
      if (!res.ok) throw new Error("Failed to load clients");
      const json = await res.json();
      return json.data as Client[];
    },
  });

  // Active (non-archived) clients only — archived ids would 404 the fan-out.
  const activeClients = useMemo(
    () => clients.filter((c) => !c.archivedAt),
    [clients],
  );
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = q ? activeClients.filter((c) => c.name.toLowerCase().includes(q)) : activeClients;
    return [...list].sort((a, b) => a.name.localeCompare(b.name));
  }, [activeClients, search]);

  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const atCap = selected.length >= ROLLUP_MEMBER_CAP;

  function toggle(clientId: string) {
    if (selectedSet.has(clientId)) {
      onChange(selected.filter((id) => id !== clientId));
    } else {
      if (atCap) return; // hard cap — block beyond ROLLUP_MEMBER_CAP
      onChange([...selected, clientId]);
    }
  }

  return (
    <div className="mt-6" data-testid="rollup-portfolio-picker">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-[13px] font-semibold">Pick clients</h4>
        <span className="text-[11px] text-muted-foreground" data-testid="rollup-portfolio-count">
          {selected.length} selected{selected.length > 0 ? ` (max ${ROLLUP_MEMBER_CAP})` : ""}
        </span>
      </div>

      <div className="relative mb-2">
        <MagnifyingGlass size={16} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search clients..."
          data-testid="rollup-portfolio-search"
          className="w-full pl-8 pr-3 py-2 text-[13px] rounded-lg bg-background shadow-xs focus:outline-none"
          style={{ border: "0.5px solid var(--hairline)" }}
        />
      </div>

      <div className="max-h-72 overflow-y-auto space-y-1 rounded-lg p-1" style={{ border: "0.5px solid var(--hairline)" }}>
        {isLoading ? (
          <p className="text-sm text-muted-foreground py-4 text-center">Loading clients…</p>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">
            {search.trim() ? "No clients match your search." : "No clients yet."}
          </p>
        ) : (
          filtered.map((client) => {
            const isMember = selectedSet.has(client.id);
            const blocked = !isMember && atCap;
            return (
              <button
                key={client.id}
                type="button"
                data-testid={`rollup-portfolio-option-${client.id}`}
                onClick={() => toggle(client.id)}
                disabled={blocked}
                aria-pressed={isMember}
                className="w-full flex items-center gap-3 rounded-lg px-2 py-2 text-[13px] hover:bg-accent cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <span
                  className={`h-4 w-4 shrink-0 rounded-[5px] border-[0.5px] flex items-center justify-center ${
                    isMember ? "bg-primary border-primary" : "border-input"
                  }`}
                >
                  {isMember && <Check size={12} weight="bold" className="text-primary-foreground" />}
                </span>
                <span className="flex-1 text-left truncate">{client.name}</span>
                {client.demoMode && (
                  <span className="text-[10.5px] text-muted-foreground shrink-0">Demo</span>
                )}
              </button>
            );
          })
        )}
      </div>

      {atCap && (
        <p className="mt-2 text-[12px] text-amber-700" data-testid="rollup-portfolio-cap-warning">
          You've reached the {ROLLUP_MEMBER_CAP}-client limit for a roll-up.
        </p>
      )}
      {selected.length === 0 && (
        <p className="mt-2 text-[12px] text-muted-foreground" data-testid="rollup-portfolio-empty-hint">
          Select at least one client to continue.
        </p>
      )}
    </div>
  );
}
